import ThemedElement from "shared/components/ThemedElement";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  CircularProgress,
  Alert,
  InputAdornment,
  Card,
  Tooltip,
  Snackbar,
  IconButton,
  Box,
  Grid,
  Chip,
  Paper,
  TextField,
  alpha,
  MenuItem,
  Button,
} from "@mui/material";
import {
  Search,
  Refresh,
  Receipt,
  Download,
  CheckCircle,
  Schedule,
  Cancel,
  Error,
  Info,
  Link as LinkIcon,
  CreditCard,
  ContentCopy,
  PictureAsPdf,
  AccessTime,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import * as XLSX from "xlsx";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import DataTable from "shared/components/Tables/DataTable";
import { getRecaudoDataService } from "modules/financiera/services/recaudoService";
import { filterRecaudos, formatRecaudoDate, recaudoExportRows } from "../../model/recaudoModel";
import PaymentReceiptModal from "./PaymentReceiptModal";

const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const colors = {
  primary: "#193e54",
  secondary: "#12536a",
  success: "#217346",
  warning: "#ed6c02",
  error: "#d32f2f",
  info: "#2980b9", // Azul para "En progreso"
  expired: "#6c757d",
  white: "#ffffff",
  gray: "#f5f5f5",
  darkGray: "#333333",
  border: "#e2e8f0",
};

function TableAseguramientoContent({ refreshFlag, onError }) {
  const [recaudoData, setRecaudoData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const pending = useRef(null),
    request = useRef(0),
    mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      request.current++;
    };
  }, []);
  const fetchRecaudoData = useCallback(async () => {
    const id = ++request.current;
    setLoading(true);
    setError(null);
    if (!pending.current) {
      pending.current = getRecaudoDataService();
      const promise = pending.current;
      promise
        .finally(() => {
          if (pending.current === promise) pending.current = null;
        })
        .catch(() => {});
    }
    try {
      const result = await pending.current;
      if (!mounted.current || id !== request.current) return;
      if (!result.success) throw new Error(result.error || "Error al cargar datos de recaudo");
      setRecaudoData(result.data || []);
      onErrorRef.current?.(null);
    } catch (err) {
      if (mounted.current && id === request.current) {
        setError(err.message || "Error al cargar recaudos");
        onErrorRef.current?.(err.message || "Error al cargar recaudos");
      }
    } finally {
      if (mounted.current && id === request.current) setLoading(false);
    }
  }, []);
  const refreshDataPreservePage = fetchRecaudoData;
  const filteredData = useMemo(
    () =>
      filterRecaudos(recaudoData, {
        search: searchQuery,
        start: startDate,
        end: endDate,
        status: statusFilter,
      }),
    [recaudoData, searchQuery, startDate, endDate, statusFilter]
  );
  const invalidDates = Boolean(startDate && endDate && startDate > endDate);
  useEffect(() => {
    fetchRecaudoData();
  }, [refreshFlag, fetchRecaudoData]);
  useEffect(() => {
    window.addEventListener("refreshRecaudo", fetchRecaudoData);
    return () => window.removeEventListener("refreshRecaudo", fetchRecaudoData);
  }, [fetchRecaudoData]);

  const clearAllFilters = () => {
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
    setStatusFilter("todos");
  };

  const hasActiveFilters =
    searchQuery || startDate || endDate || (statusFilter && statusFilter !== "todos");

  const exportToExcel = () => {
    if (filteredData.length === 0) {
      setSnackbar({ open: true, message: "No hay datos para exportar", severity: "warning" });
      return;
    }

    try {
      const ws = XLSX.utils.json_to_sheet(recaudoExportRows(filteredData));
      ws["!cols"] = [8, 18, 40, 20, 18, 24, 35, 50, 12, 20, 25].map((wch) => ({ wch }));
      ws["!autofilter"] = { ref: ws["!ref"] };
      filteredData.forEach((_, index) => {
        const cell = ws[`D${index + 2}`];
        if (cell) cell.z = '"$" #,##0.00';
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Recaudos");
      XLSX.writeFile(
        wb,
        `recaudos_${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.xlsx`
      );

      setSnackbar({
        open: true,
        message: `Exportados ${filteredData.length} registros`,
        severity: "success",
      });
    } catch (error) {
      setSnackbar({ open: true, message: "Error al exportar los datos", severity: "error" });
    }
  };

  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const handleOpenReceipt = (row) => {
    setSelectedTransaction(row);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTransaction(null);
  };

  const handleModalSuccess = (message) => {
    setSnackbar({ open: true, message: message, severity: "success" });
  };

  const getStatusIcon = (category) => {
    switch (category) {
      case "conciliated":
      case "approved":
        return <CheckCircle sx={{ fontSize: 14 }} />;
      case "rejected":
        return <Cancel sx={{ fontSize: 14 }} />;
      case "expired":
        return <AccessTime sx={{ fontSize: 14 }} />;
      case "pending_customer":
        return <Info sx={{ fontSize: 14 }} />;
      case "pending":
      default:
        return <Schedule sx={{ fontSize: 14 }} />;
    }
  };

  const getChipColor = (category) => {
    switch (category) {
      case "conciliated":
        return "info";
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "expired":
        return "default";
      case "pending_customer":
        return "info"; // Color azul para "En progreso"
      case "pending":
      default:
        return "warning";
    }
  };

  const getChipSx = (category) => {
    if (category === "expired") {
      return {
        fontWeight: 500,
        backgroundColor: colors.expired,
        color: colors.white,
        "& .MuiChip-icon": {
          fontSize: 14,
          marginLeft: 0.5,
          color: colors.white,
        },
        "& .MuiChip-label": {
          color: colors.white,
        },
      };
    }
    if (category === "pending_customer") {
      return {
        fontWeight: 500,
        backgroundColor: colors.info,
        color: colors.white,
        "& .MuiChip-icon": {
          fontSize: 14,
          marginLeft: 0.5,
          color: colors.white,
        },
        "& .MuiChip-label": {
          color: colors.white,
        },
      };
    }
    return {
      fontWeight: 500,
      "& .MuiChip-icon": { fontSize: 14, marginLeft: 0.5 },
    };
  };

  const renderPaymentId = useCallback(({ value }) => {
    const rawDisplay = value?.display ?? value?.fullId ?? value?.value;
    if (rawDisplay === null || rawDisplay === undefined || rawDisplay === "") {
      return (
        <ThemedElement component="span" style={{ color: "#999" }}>
          -
        </ThemedElement>
      );
    }

    const isLink = value.isLink;
    const displayValue = String(rawDisplay);
    const tooltipText = value.tooltip;
    const fullId = String(value.fullId ?? "") || displayValue;

    const truncatedDisplay =
      displayValue.length > 25
        ? `${displayValue.substring(0, 20)}...${displayValue.substring(displayValue.length - 5)}`
        : displayValue;

    const handleCopy = async (e) => {
      e.stopPropagation();
      e.preventDefault();
      try {
        await navigator.clipboard.writeText(fullId);
        setSnackbar((prev) => ({
          ...prev,
          open: true,
          message: `${isLink ? "Link" : "ID de pago"} copiado al portapapeles`,
          severity: "success",
        }));
      } catch (err) {
        setSnackbar((prev) => ({
          ...prev,
          open: true,
          message: "Error al copiar",
          severity: "error",
        }));
      }
    };

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
        <Tooltip title={tooltipText || fullId} placement="top">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              fontFamily: "monospace",
              fontSize: "0.75rem",
              backgroundColor: isLink ? alpha(colors.info, 0.1) : alpha(colors.primary, 0.1),
              padding: "2px 6px",
              borderRadius: "12px",
              minWidth: 0,
              overflow: "hidden",
              width: "fit-content",
            }}
          >
            {isLink ? (
              <LinkIcon sx={{ fontSize: 14, color: colors.info }} />
            ) : (
              <CreditCard sx={{ fontSize: 14, color: colors.primary }} />
            )}
            <ThemedElement
              component="span"
              style={{
                color: isLink ? colors.info : colors.primary,
                fontWeight: 500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {truncatedDisplay}
            </ThemedElement>
          </Box>
        </Tooltip>
        <Tooltip title="Copiar al portapapeles" placement="top">
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{
              padding: "4px",
              backgroundColor: alpha(colors.primary, 0.08),
              "&:hover": {
                backgroundColor: alpha(colors.primary, 0.15),
              },
            }}
          >
            <ContentCopy sx={{ fontSize: 14, color: colors.primary }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }, []);

  const renderStatusBadge = useCallback(({ value }) => {
    if (!value) return null;

    let tooltipText = `${value.text} · ${value.originalStatus || ""}`;
    if (value.category === "rejected" && value.rejectionReason) {
      tooltipText = `Rechazado: ${value.rejectionReason}`;
    } else if (value.category === "expired" && value.rejectionReason) {
      tooltipText = value.rejectionReason;
    }

    return (
      <Tooltip title={tooltipText} placement="top">
        <Chip
          icon={getStatusIcon(value.category)}
          label={value.text}
          size="small"
          color={getChipColor(value.category)}
          sx={getChipSx(value.category)}
        />
      </Tooltip>
    );
  }, []);

  const renderAmount = useCallback(({ value }) => formatCurrency(value), []);

  const renderActions = useCallback(({ value }) => {
    if (!value || !["approved", "conciliated"].includes(value.statusCategory)) {
      return null;
    }

    return (
      <Tooltip title="Ver comprobante de pago" placement="top">
        <Button
          variant="outlined"
          size="small"
          startIcon={<PictureAsPdf />}
          onClick={() => handleOpenReceipt(value)}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            whiteSpace: "nowrap",
            minWidth: 0,
            px: 0.75,
            "& .MuiButton-startIcon": { mr: 0.5 },
            borderColor: colors.error,
            color: colors.error,
            "&:hover": {
              borderColor: colors.error,
              backgroundColor: alpha(colors.error, 0.1),
            },
          }}
        >
          PDF
        </Button>
      </Tooltip>
    );
  }, []);

  const renderRejectionReason = useCallback(({ value }) => {
    if (!value)
      return (
        <ThemedElement component="span" style={{ color: "#999" }}>
          -
        </ThemedElement>
      );

    const rejectionReason = value.rejectionReason;

    if (!rejectionReason)
      return (
        <ThemedElement component="span" style={{ color: "#999" }}>
          -
        </ThemedElement>
      );

    const isExpired = value.isLink && value.rejectionReason?.includes("expiró");
    const isPendingCustomer = value.rejectionReason === "Pendiente por cliente";

    return (
      <Tooltip title={rejectionReason} placement="top">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            backgroundColor: isExpired
              ? alpha(colors.expired, 0.1)
              : isPendingCustomer
              ? alpha(colors.info, 0.1)
              : alpha(colors.error, 0.1),
            padding: "2px 8px",
            borderRadius: "12px",
            width: "fit-content",
          }}
        >
          <Error
            sx={{
              fontSize: 14,
              color: isExpired ? colors.expired : isPendingCustomer ? colors.info : colors.error,
            }}
          />
          <ThemedElement
            component="span"
            style={{
              color: isExpired ? colors.expired : isPendingCustomer ? colors.info : colors.error,
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            {rejectionReason.length > 30
              ? `${rejectionReason.substring(0, 27)}...`
              : rejectionReason}
          </ThemedElement>
        </Box>
      </Tooltip>
    );
  }, []);

  // ============ COLUMNAS ============
  const columns = useMemo(
    () => [
      { Header: "ID", accessor: "id", width: "5%" },
      { Header: "NIT", accessor: "nit", width: "9%" },
      { Header: "Empresa", accessor: "company", width: "16%" },
      { Header: "Valor", accessor: "amount", width: "10%", Cell: renderAmount },
      { Header: "Estado", accessor: "statusBadge", width: "9%", Cell: renderStatusBadge },
      {
        Header: "Motivo",
        accessor: "rejectionReasonRender",
        width: "10%",
        Cell: renderRejectionReason,
      },
      {
        Header: "ID Pago / Link",
        accessor: "paymentIdRender",
        width: "16%",
        Cell: renderPaymentId,
      },
      { Header: "N° Factura", accessor: "billNumber", width: "7%" },
      // Fecha de creación en la misma zona horaria utilizada por los filtros y el Excel.
      {
        Header: "Creación · Bogotá",
        accessor: "createdAt",
        width: "11%",
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => {
          const formattedDate = formatRecaudoDate(value);
          return <span>{formattedDate}</span>;
        },
      },
      // ======================================================================
      { Header: "Acciones", accessor: "actions", width: "7%", Cell: renderActions },
    ],
    [renderAmount, renderStatusBadge, renderPaymentId, renderActions, renderRejectionReason]
  );

  // ============ ROWS ============
  const rows = useMemo(() => {
    return filteredData.map((item) => ({
      id: item.id,
      nit: item.nit,
      company: item.company,
      amount: item.amount,
      statusBadge: {
        text: item.statusText,
        color: item.statusColor,
        category: item.statusCategory,
        detail: item.statusDetail,
        originalStatus: item.status,
        rejectionReason: item.rejectionReason,
      },
      rejectionReasonRender: {
        rejectionReason: item.rejectionReason,
        isLink: item.isLinkPayment,
      },
      paymentIdRender: {
        value: item.paymentId,
        display: item.paymentIdDisplay,
        isLink: item.paymentIdIsLink,
        tooltip: item.paymentIdTooltip,
        fullId: item.paymentId,
      },
      billNumber: item.billNumber !== "N/A" ? item.billNumber : "-",
      createdAt: item.createdAt,
      actions: {
        statusCategory: item.statusCategory,
        original: item,
      },
      original: item,
    }));
  }, [filteredData]);

  const stats = useMemo(() => {
    return {
      total: filteredData.length,
      totalAmount: filteredData.reduce((sum, item) => sum + (item.amount || 0), 0),
      approvedCount: filteredData.filter((item) => item.statusCategory === "approved").length,
      conciliatedCount: filteredData.filter((item) => item.statusCategory === "conciliated").length,
      approvedAmount: filteredData
        .filter((item) => item.statusCategory === "approved")
        .reduce((sum, item) => sum + item.amount, 0),
      conciliatedAmount: filteredData
        .filter((item) => item.statusCategory === "conciliated")
        .reduce((sum, item) => sum + item.amount, 0),
      rejectedCount: filteredData.filter((item) => item.statusCategory === "rejected").length,
      pendingCount: filteredData.filter((item) => item.statusCategory === "pending").length,
      pendingCustomerCount: filteredData.filter(
        (item) => item.statusCategory === "pending_customer"
      ).length,
      expiredCount: filteredData.filter((item) => item.statusCategory === "expired").length,
      linksCount: filteredData.filter((item) => item.paymentIdIsLink).length,
      paymentsCount: filteredData.filter((item) => !item.paymentIdIsLink && item.paymentId).length,
    };
  }, [filteredData]);

  return (
    <MDBox>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <PaymentReceiptModal
        open={modalOpen}
        onClose={handleCloseModal}
        transactionData={selectedTransaction?.original || selectedTransaction}
        onSuccess={handleModalSuccess}
      />

      <Card sx={{ borderRadius: 2, overflow: "hidden", boxShadow: "0 2px 8px #193e5410", mb: 2 }}>
        <Box
          sx={{
            p: 2.5,
            bgcolor: colors.primary,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Receipt sx={{ color: "#fff", fontSize: 28 }} />
          <Box sx={{ flex: "1 1 240px" }}>
            <MDTypography
              component="h1"
              sx={{ color: "#fff", fontSize: "1.15rem", fontWeight: 700 }}
            >
              Recaudo
            </MDTypography>
            <MDTypography sx={{ color: "#dbe8f2", fontSize: ".8rem" }}>
              Pagos, conciliación y comprobantes · Valores en COP
            </MDTypography>
          </Box>
          <Button
            startIcon={<Download />}
            onClick={exportToExcel}
            disabled={loading || !filteredData.length || invalidDates}
            sx={{ color: "#fff", "&.Mui-disabled": { color: "#a7bbca" } }}
          >
            Exportar Excel
          </Button>
          <Button
            startIcon={<Refresh />}
            onClick={fetchRecaudoData}
            disabled={loading}
            sx={{ color: "#fff", "&.Mui-disabled": { color: "#a7bbca" } }}
          >
            {loading ? "Actualizando…" : "Actualizar"}
          </Button>
        </Box>
        <Box sx={{ p: 2.5 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
                xl: "repeat(7, 1fr)",
              },
              gap: 1.5,
              mb: 2,
            }}
          >
            {[
              ["Total", stats.total, colors.primary],
              ["Aprobados", stats.approvedCount, colors.success],
              ["Conciliados", stats.conciliatedCount, colors.info],
              ["Rechazados", stats.rejectedCount, colors.error],
              ["Pendientes", stats.pendingCount, colors.warning],
              ["En progreso", stats.pendingCustomerCount, colors.info],
              ["Links vencidos", stats.expiredCount, colors.expired],
            ].map(([label, count, color]) => (
              <Paper
                key={label}
                component={label === "Links vencidos" ? "button" : "div"}
                type={label === "Links vencidos" ? "button" : undefined}
                aria-label={label === "Links vencidos" ? "Ver links vencidos" : undefined}
                onClick={label === "Links vencidos" ? () => setStatusFilter("vencidos") : undefined}
                elevation={0}
                sx={{
                  p: 1.5,
                  textAlign: "left",
                  ...(label === "Links vencidos"
                    ? {
                        cursor: "pointer",
                        "&:hover": { bgcolor: alpha(color, 0.12) },
                        "&:focus-visible": {
                          outline: `2px solid ${colors.primary}`,
                          outlineOffset: 2,
                        },
                      }
                    : {}),
                  bgcolor: alpha(color, 0.06),
                  border: `1px solid ${alpha(color, 0.15)}`,
                  borderRadius: 2,
                }}
              >
                <MDTypography sx={{ fontSize: ".75rem", color: "#526579" }}>{label}</MDTypography>
                <MDTypography sx={{ fontSize: "1.4rem", fontWeight: 700, color }}>
                  {count.toLocaleString("es-CO")}
                </MDTypography>
              </Paper>
            ))}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 3,
              mb: 3,
              p: 1.5,
              bgcolor: "#f5f8fb",
              borderRadius: 2,
            }}
          >
            {[
              ["Valor de los registros", stats.totalAmount],
              ["Valor aprobado", stats.approvedAmount],
              ["Valor conciliado", stats.conciliatedAmount],
            ].map(([label, value]) => (
              <Box key={label}>
                <MDTypography sx={{ fontSize: ".72rem", color: "#526579" }}>{label}</MDTypography>
                <MDTypography sx={{ fontSize: ".9rem", fontWeight: 600, color: "#243b53" }}>
                  {formatCurrency(value)}
                </MDTypography>
              </Box>
            ))}
          </Box>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                label="Buscar ID, NIT, empresa, factura o pago"
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Desde · Bogotá"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Hasta · Bogotá"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={invalidDates}
                helperText={invalidDates ? "Debe ser igual o posterior a Desde" : undefined}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                select
                label="Estado"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ "& .MuiInputBase-root": { minHeight: 37 } }}
              >
                {[
                  ["todos", "Todos"],
                  ["aprobados", "Aprobados / pagados"],
                  ["conciliados", "Conciliados"],
                  ["rechazados", "Rechazados"],
                  ["pendientes", "Pendientes"],
                  ["enprogreso", "En progreso"],
                  ["vencidos", "Links vencidos"],
                ].map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mt: 1.5,
            }}
          >
            <MDTypography sx={{ fontSize: ".75rem", color: "#526579" }}>
              {filteredData.length.toLocaleString("es-CO")} de{" "}
              {recaudoData.length.toLocaleString("es-CO")} registros · Totales según los filtros
            </MDTypography>
            <Button onClick={clearAllFilters} disabled={!hasActiveFilters} size="small">
              Limpiar filtros
            </Button>
          </Box>
        </Box>
      </Card>

      {error && recaudoData.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error} Se conservan los últimos datos disponibles.
        </Alert>
      )}
      {loading && !recaudoData.length ? (
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress sx={{ color: colors.primary }} />
        </MDBox>
      ) : error && !recaudoData.length ? (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: "6px" }}
          action={
            <MDButton color="inherit" size="small" onClick={fetchRecaudoData}>
              Reintentar
            </MDButton>
          }
        >
          {error}
        </Alert>
      ) : filteredData.length === 0 ? (
        <MDBox p={3} textAlign="center">
          <Receipt sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }} />
          <MDTypography variant="h6" sx={{ color: "#64748b" }}>
            {hasActiveFilters
              ? "No se encontraron resultados con los filtros aplicados"
              : "No hay registros de recaudo"}
          </MDTypography>
          {hasActiveFilters && (
            <MDButton variant="outlined" onClick={clearAllFilters} sx={{ mt: 2 }}>
              Limpiar filtros
            </MDButton>
          )}
        </MDBox>
      ) : (
        <Box
          sx={{
            border: "1px solid #dce5ed",
            borderRadius: 2,
            overflow: "hidden",
            "& thead": { display: "table-header-group", p: 0 },
            "& table": { minWidth: 1180, tableLayout: "fixed" },
            "& td, & th": { px: 1, py: 1.5 },
            "& td": { fontSize: ".8rem", color: "#243b53", overflowWrap: "anywhere" },
            "& td > div": { width: "100%", whiteSpace: "normal", color: "#243b53" },
            "& th > div": { color: "#354d63", opacity: 1, paddingRight: "14px" },
            "& th > div > div": { right: 0 },
            "& th": { bgcolor: "#eef3f8" },
          }}
        >
          <DataTable
            key="recaudo-table-static"
            table={{ columns, rows }}
            isSorted={true}
            entriesPerPage={{ defaultValue: 25, entries: [10, 25, 50, 100, 200] }}
            showTotalEntries={true}
            noEndBorder
            canSearch={false}
            autoResetPage={false}
            paginationResetKey={`${searchQuery}|${startDate}|${endDate}|${statusFilter}`}
          />
        </Box>
      )}
    </MDBox>
  );
}

TableAseguramientoContent.propTypes = {
  refreshFlag: PropTypes.bool,
  onError: PropTypes.func,
};

TableAseguramientoContent.defaultProps = {
  refreshFlag: false,
  onError: null,
};

export default TableAseguramientoContent;
